import { gql, type TypedDocumentNode } from '@apollo/client';

import { WidgetChip } from '@sorare/core/src/atoms/ui/Chip/WidgetChip/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { DumbFeaturedCollectionTaskWidget } from '#src/components/missions/DumbFeaturedCollectionTaskWidget/index.tsx';

import type { ProFeaturedCollectionTaskWidget_currentUser } from './__generated__/index.graphql.ts';

type Props = {
  to: string;
  onDesktopClick?: () => void;
  currentUser?: ProFeaturedCollectionTaskWidget_currentUser | null;
};

export const ProFeaturedCollectionTaskWidget = ({
  to,
  onDesktopClick,
  currentUser,
}: Props) => {
  const { sport } = useSportContext();
  const isDesktop = useIsDesktopAndAbove();
  const {
    flags: {
      enableSorare27Nav = { BASEBALL: false, FOOTBALL: false, NBA: false },
    },
  } = useFeatureFlags();

  if (!enableSorare27Nav[sport]) {
    return null;
  }

  const hasUnclaimedTasks =
    (currentUser?.myCollectUnclaimedTasksCount ?? 0) > 0;

  const chip = hasUnclaimedTasks ? <WidgetChip state="reward" /> : undefined;

  if (isDesktop && onDesktopClick) {
    return (
      <DumbFeaturedCollectionTaskWidget
        chip={chip}
        size={5}
        progresses={[1]}
        onClick={onDesktopClick}
      />
    );
  }

  return (
    <DumbFeaturedCollectionTaskWidget
      chip={chip}
      size={5}
      progresses={[1]}
      to={to}
    />
  );
};

ProFeaturedCollectionTaskWidget.fragments = {
  currentUser: gql`
    fragment ProFeaturedCollectionTaskWidget_currentUser on CurrentUser {
      slug
      myCollectUnclaimedTasksCount: myUnclaimedTasksCount(
        sport: $sport
        group: "collect"
      )
    }
  ` as TypedDocumentNode<ProFeaturedCollectionTaskWidget_currentUser>,
};
